import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Link2, Pencil } from 'lucide-react';
import { API, authHeader, CATEGORY_CONFIG } from '@/components/library/libraryMeta';

const fieldCls = 'bg-[hsl(var(--surface))] border-[hsl(var(--border))]';
const Label = ({ children }) => <label className="text-xs text-[hsl(var(--text-muted))] mb-1 block">{children}</label>;

// Add Link (item=null) or Edit/Move an existing file or link.
export const ItemFormDialog = ({ open, onOpenChange, item, folderId, folderLabel, onSubmit }) => {
  const isLink = !item || item.kind !== 'file';
  const [form, setForm] = useState(() => ({
    title: item?.title || '', url: item?.url || '', description: item?.description || '',
    category: item?.category || 'general', folder_id: item ? item.folder_id || 'root' : folderId || 'root',
  }));
  const [folders, setFolders] = useState([]);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!item) return;
    axios.get(`${API}/library/folders`, authHeader()).then((r) => setFolders(r.data.folders || [])).catch(() => {});
  }, [item]);

  const submit = async () => {
    setSaving(true);
    try {
      const payload = { ...form, folder_id: form.folder_id === 'root' ? null : form.folder_id };
      if (!isLink) delete payload.url;
      if (await onSubmit(payload)) onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  const valid = form.title.trim() && (!isLink || /^https?:\/\//i.test(form.url.trim()));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-md" data-testid="item-form-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {item ? <Pencil size={17} className="text-cyan-400" /> : <Link2 size={18} className="text-cyan-400" />}
            {item ? 'Edit item' : 'Add link'}
          </DialogTitle>
          {!item && <DialogDescription>To: Library{folderLabel ? ` / ${folderLabel}` : ''}</DialogDescription>}
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Title *</Label><Input value={form.title} onChange={(e) => set('title', e.target.value)} className={fieldCls} data-testid="item-title-input" /></div>
          {isLink && <div><Label>URL *</Label><Input value={form.url} placeholder="https://..." onChange={(e) => set('url', e.target.value)} className={fieldCls} data-testid="item-url-input" /></div>}
          <div>
            <Label>Description</Label>
            <textarea value={form.description} rows={2} onChange={(e) => set('description', e.target.value)} placeholder="Brief description..."
              className={`w-full rounded-md border p-2.5 text-sm resize-none text-[hsl(var(--text-primary))] ${fieldCls}`} data-testid="item-description-input" />
          </div>
          <div className={`grid gap-3 ${item ? 'grid-cols-2' : 'grid-cols-1'}`}>
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => set('category', v)}>
                <SelectTrigger className={fieldCls} data-testid="item-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(CATEGORY_CONFIG).map(([k, c]) => <SelectItem key={k} value={k}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {item && (
              <div>
                <Label>Folder</Label>
                <Select value={form.folder_id} onValueChange={(v) => set('folder_id', v)}>
                  <SelectTrigger className={fieldCls} data-testid="item-folder-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="root">Library (top level)</SelectItem>
                    {folders.map((f) => <SelectItem key={f.id} value={f.id} data-testid={`item-folder-option-${f.id}`}>{f.path}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="item-cancel-btn">Cancel</Button>
          <Button onClick={submit} disabled={saving || !valid} className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white" data-testid="item-save-btn">
            {saving && <Loader2 size={15} className="animate-spin mr-2" />} {item ? 'Save' : 'Add link'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
